'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface CustomSelectProps {
  value: string;
  setValue: (val: string) => void;
  options: { value: string, label: string }[];
  placeholder: string;
}

const CustomSelect = ({ value, setValue, options, placeholder }: CustomSelectProps) => {
  const selectedLabel = options.find(opt => opt.value === value)?.label || placeholder;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full bg-white box-sizing-border-box px-2 py-2 border rounded-xl focus:ring-2 flex justify-between items-center text-base md:text-sm focus:outline-none placeholder:text-zinc-500
            ${value ? 'border-zinc-900 ring-zinc-900 text-zinc-900 font-semibold' : 'border-zinc-400 ring-zinc-900 text-zinc-500'}
          `}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${value ? 'text-zinc-900' : 'text-zinc-500'}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[9999] w-[var(--radix-popover-trigger-width)] p-1 bg-white shadow-lg rounded-md border border-zinc-200">
        {options.map(option => (
          <div
            key={option.value}
            onClick={() => {
              setValue(option.value);
              setIsOpen(false);
            }}
            className="text-base md:text-sm text-zinc-800 px-3 py-2 rounded-md hover:bg-zinc-100 cursor-pointer flex justify-between items-center"
          >
            <span className="truncate">{option.label}</span>
            {value === option.value && <Check className="h-4 w-4 text-zinc-900" />}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};

interface PropertyTransactionFormProps {
  dictionary: any;
  disableSubmit?: boolean;
  agencyLanguage?: string;
  askAsbestosAndFinishLevel?: boolean;
  onSubmit: (propertyInfo: {
    epcValue: string;
    condition: string;
    asbestos?: string;
    finishLevel?: string;
  }) => void;
  transactionCount?: number;
  // Prefill for session-resume
  initialValues?: Partial<{
    epcValue: string;
    asbestos: string;
    condition: string;
    finishLevel: string;
  }>;
}

export default function PropertyTransactionForm({ dictionary, onSubmit, disableSubmit = false, transactionCount, agencyLanguage, askAsbestosAndFinishLevel = false, initialValues }: PropertyTransactionFormProps) {
  const [errors, setErrors] = useState<{
    epcValue?: string;
    condition?: string;
    asbestos?: string;
    finishLevel?: string;
  }>({});
  
  // State for all four dropdowns
  const [epcValue, setEpcValue] = useState('');
  const [asbestosValue, setAsbestosValue] = useState('');
  const [conditionValue, setConditionValue] = useState('');
  const [finishLevelValue, setFinishLevelValue] = useState('');
  // Local bottom anchor to request an in-container scroll when new blocks appear
  const bottomRef = useRef<HTMLDivElement>(null);
  
  // State to manage which part of the form is visible
  // const [formStep, setFormStep] = useState(1);

  // Prefill from initialValues
  useEffect(() => {
    try {
      if (!initialValues) return;
      if (typeof initialValues.epcValue === 'string') setEpcValue(initialValues.epcValue);
      if (typeof initialValues.asbestos === 'string') setAsbestosValue(initialValues.asbestos);
      if (typeof initialValues.condition === 'string') setConditionValue(initialValues.condition);
      if (typeof initialValues.finishLevel === 'string') setFinishLevelValue(initialValues.finishLevel);
    } catch (_) {}
  }, [initialValues]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const propertyInfo: {
      epcValue: string;
      condition: string;
      asbestos?: string;
      finishLevel?: string;
    } = {
      epcValue,
      condition: conditionValue,
    };

    if (askAsbestosAndFinishLevel) {
      propertyInfo.asbestos = asbestosValue;
      propertyInfo.finishLevel = finishLevelValue;
    }

    const newErrors: typeof errors = {};

    if (!propertyInfo.epcValue) newErrors.epcValue = dictionary?.sellingForm?.requiredField;
    if (askAsbestosAndFinishLevel && agencyLanguage !== 'fr' && !propertyInfo.asbestos) newErrors.asbestos = dictionary?.sellingForm?.requiredField;
    if (!propertyInfo.condition) newErrors.condition = dictionary?.sellingForm?.requiredField;
    if (askAsbestosAndFinishLevel && !propertyInfo.finishLevel) newErrors.finishLevel = dictionary?.sellingForm?.requiredField;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit(propertyInfo);
  };


  const epcOptions = [
    { value: 'A', label: dictionary?.sellingForm?.propertyEpcLabelA || 'EPC A' },
    { value: 'B', label: dictionary?.sellingForm?.propertyEpcLabelB || 'EPC B' },
    { value: 'C', label: dictionary?.sellingForm?.propertyEpcLabelC || 'EPC C' },
    { value: 'D', label: dictionary?.sellingForm?.propertyEpcLabelD || 'EPC D' },
    { value: 'E', label: dictionary?.sellingForm?.propertyEpcLabelE || 'EPC E' },
    { value: 'F', label: dictionary?.sellingForm?.propertyEpcLabelF || 'EPC F' },
    { value: 'G', label: dictionary?.sellingForm?.propertyEpcLabelG || 'EPC G' },
    { value: 'unknown', label: dictionary?.sellingForm?.propertyEpcUnknown || 'Unknown' },
  ];

  const asbestosOptions = [
    { value: 'none', label: dictionary?.sellingForm?.asbestosNone || 'None' },
    { value: 'not_much', label: dictionary?.sellingForm?.asbestosNotMuch || 'Not much' },
    { value: 'lots', label: dictionary?.sellingForm?.asbestosLots || 'Lots' },
    { value: 'dont_know', label: dictionary?.sellingForm?.asbestosDontKnow || 'Not known' },
  ];

  const conditionOptions = [
    { value: '1', label: dictionary?.propertyForm?.condition1 || 'State: 5/5 As new' },
    { value: '2', label: dictionary?.propertyForm?.condition2 || 'State: 4/5 Very good' },
    { value: '3', label: dictionary?.propertyForm?.condition3 || 'State: 3/5 Signs of use' },
    { value: '4', label: dictionary?.propertyForm?.condition4 || 'State: 2/5 Minor defects' },
    { value: '5', label: dictionary?.propertyForm?.condition5 || 'State: 1/5 Major defects' },
    { value: '6', label: dictionary?.propertyForm?.condition6 || 'State: 0/5 Uninhabitable' },
  ];

  const finishLevelOptions = [
    { value: 'poor', label: dictionary?.sellingForm?.finishLevelPoor || 'Poorly finished' },
    { value: 'moderate', label: dictionary?.sellingForm?.finishLevelModerate || 'Moderately finished' },
    { value: 'good', label: dictionary?.sellingForm?.finishLevelGood || 'Well finished' },
    { value: 'very_good', label: dictionary?.sellingForm?.finishLevelVeryGood || 'Very well finished' },
  ];

  // Smoothly keep the newest part of the form in view when extra fields appear
  useEffect(() => {
    const shouldShowAsbestos = askAsbestosAndFinishLevel && agencyLanguage !== 'fr';
    const extraVisible = Boolean(
      epcValue && (shouldShowAsbestos ? asbestosValue : true)
    );
    if (!extraVisible) return;

    const doScroll = () => {
      // Prefer scrolling the shared messages container (more reliable on desktop)
      const container = document.querySelector('[data-avm-messages="true"]') as HTMLElement | null;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Delay to ensure popover/layout finishes painting
    const id = window.setTimeout(doScroll, 50);
    return () => window.clearTimeout(id);
  }, [epcValue, asbestosValue, agencyLanguage, askAsbestosAndFinishLevel]);

  // When the submit button becomes visible (all required selections made), ensure it's in view
  useEffect(() => {
    const shouldShowAsbestos = askAsbestosAndFinishLevel && agencyLanguage !== 'fr';
    const shouldShowFinishLevel = askAsbestosAndFinishLevel;
    
    const buttonVisible = Boolean(
      epcValue && 
      conditionValue && 
      (!shouldShowAsbestos || asbestosValue) && 
      (!shouldShowFinishLevel || finishLevelValue)
    );
    if (!buttonVisible) return;

    const doScroll = () => {
      const container = document.querySelector('[data-avm-messages="true"]') as HTMLElement | null;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    const id = window.setTimeout(doScroll, 50);
    return () => window.clearTimeout(id);
  }, [epcValue, asbestosValue, agencyLanguage, conditionValue, finishLevelValue, askAsbestosAndFinishLevel]);

  return (
    // Use full width and remove mb-4 to let parent control bottom margin
    <form onSubmit={handleSubmit} className="w-full">
      {/* Adjusted text styling and spacing */}
      <div className="space-y-1">
        <p className="text-base md:text-sm text-zinc-800">
          {dictionary?.sellingForm?.transactionMessage2}
        </p>
      </div>

      {/* --- EPC Dropdown --- */}
      <div className="w-[75%] mt-4">
        <CustomSelect 
          value={epcValue} 
          setValue={setEpcValue} 
          options={epcOptions} 
          placeholder={dictionary?.sellingForm?.propertyEpc || 'EPC value'} 
        />
        {errors.epcValue && <p className="text-red-600 text-sm mt-1">{errors.epcValue}</p>}
      </div>

      {/* --- Asbestos Dropdown --- */}
      {askAsbestosAndFinishLevel && agencyLanguage !== 'fr' && (
        <div className="w-[75%] mt-2">
          <CustomSelect 
            value={asbestosValue} 
            setValue={setAsbestosValue} 
            options={asbestosOptions} 
            placeholder={dictionary?.sellingForm?.asbestos || 'Asbestos'} 
          />
          {errors.asbestos && <p className="text-red-600 text-sm mt-1">{errors.asbestos}</p>}
        </div>
      )}
      
      {epcValue && (askAsbestosAndFinishLevel ? (asbestosValue || agencyLanguage === 'fr') : true) && (
        <>
          {/* --- Condition Dropdown --- */}
          <div className="w-[75%] mt-2">
            <CustomSelect 
              value={conditionValue} 
              setValue={setConditionValue} 
              options={conditionOptions} 
              placeholder={dictionary?.propertyForm?.condition || 'Home Condition'} 
            />
            {errors.condition && <p className="text-red-600 text-sm mt-1">{errors.condition}</p>}
          </div>

          {/* --- Finish Level Dropdown --- */}
          {askAsbestosAndFinishLevel && (
            <div className="w-[75%] mt-2">
              <CustomSelect 
                value={finishLevelValue} 
                setValue={setFinishLevelValue} 
                options={finishLevelOptions} 
                placeholder={dictionary?.sellingForm?.finishLevel || 'Level of finish'} 
              />
              {errors.finishLevel && <p className="text-red-600 text-sm mt-1">{errors.finishLevel}</p>}
            </div>
          )}

          {conditionValue && (!askAsbestosAndFinishLevel || finishLevelValue) && (
            <div className="mt-4">
              <button type="submit" disabled={disableSubmit} className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none">
                {dictionary?.sellingForm?.continueButton || 'Continue'}
              </button>
            </div>
          )}
          {/* Bottom anchor to help the container scroll to the latest content */}
          <div ref={bottomRef} className="h-0" />
        </>
      )}
    </form>
  );
} 